import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { User, Cog, Plane, Wrench, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";

import { useScheduleCalendar } from "@/hooks/use-schedule-calendar";
import ScheduleCalendarView from "./schedule-calendar-view";
import ScheduleCalendarContent from "./schedule-calendar-content";
import MonthlyCalendarGrid from "./monthly-calendar-grid";
import WeeklyCalendarGrid from "./weekly-calendar-grid";
import {
  DailyGridLines,
  LaneDividers,
  ClippedIndicator,
  getAssignmentBarClasses
} from "./schedule-grid-components";
import {
  JobGanttChartProps,
  ProcessedAssignment,
  MonthlyCalendarRow
} from "../type";
import { ScheduleBarTooltip } from "./schedule-hover-card";
import {
  calculateLanes,
  getAssignmentPosition,
  OverviewJobSchedule,
  getPriorityColor,
  getColWidth,
  getDailyLaneHeight,
  SCHEDULE_CONSTANTS,
  getJobBarColorForTime
} from "../utils";

// ── JobBarContent ─────────────────────────────────────────────────────────────
// Reusable inner body for job assignment bars (daily + weekly views).
// Shows time/process/job rows always; icon rows appear only when bar is tall enough.
export interface JobBarContentProps {
  startDate: string;
  eddDate: string;
  duration?: number | null;
  processDesc: string;
  jobOrderNo: string;
  partNo?: string | null;
  partDescription?: string | null;
  customerName?: string | null;
  technicianName?: string | null;
  machineName?: string | null;
  barHeight: number;
  /** When true shows a leading "N days" label before the time range (weekly view) */
  showDaySpan?: boolean;
  hideTechnicianRow?: boolean;
  hideMachineRow?: boolean;
  textClass?: string;
  formatTime: (date: string | null | undefined) => string;
}

export function JobBarContent({
  startDate,
  eddDate,
  duration,
  processDesc,
  jobOrderNo,
  partNo,
  partDescription,
  customerName,
  technicianName,
  machineName,
  barHeight,
  showDaySpan = false,
  hideTechnicianRow = false,
  hideMachineRow = false,
  textClass = "aero:text-green-900",
  formatTime
}: JobBarContentProps) {
  const spanDays =
    showDaySpan && eddDate
      ? dayjs(eddDate)
          .startOf("day")
          .diff(dayjs(startDate).startOf("day"), "day") + 1
      : 1;
  const daysLabel = spanDays > 1 ? `(${spanDays} Days) ` : "";

  return (
    <div className="aero:flex aero:flex-col aero:justify-center aero:h-full aero:overflow-hidden aero:py-0.5">
      <p
        className={`aero:text-[11px] aero:font-normal aero:leading-[14px] aero:truncate ${textClass}`}
      >
        {showDaySpan && (
          <span className="aero:font-semibold aero:text-neutral-900">
            {daysLabel}
          </span>
        )}
        {formatTime(startDate)}&nbsp;–&nbsp;{formatTime(eddDate)}
        {duration ? ` (${Math.round(duration)}h)` : ""}
      </p>
      <p className="aero:font-bold aero:text-[13px] aero:leading-[14px] aero:truncate">
        {processDesc}
      </p>
      <p
        className={`aero:text-[11px] aero:leading-[14px] aero:truncate ${textClass}`}
      >
        {jobOrderNo}
      </p>
      {barHeight >= 80 && (
        <div className="aero:flex aero:flex-col aero:mt-1.5 aero:gap-0.5">
          <div className="aero:flex aero:items-center aero:gap-1.5">
            <Wrench className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
            {partNo ? (
              <span
                className={`aero:text-[11px] aero:leading-[14px] aero:truncate ${textClass}`}
              >
                {partNo}
                {partDescription ? ` - ${partDescription}` : ""}
              </span>
            ) : (
              <span className="aero:text-[11px] aero:leading-[14px] aero:truncate aero:italic aero:text-muted-foreground">
                No Part Number
              </span>
            )}
          </div>
          {barHeight >= 90 && (
            <div className="aero:flex aero:items-center aero:gap-1.5">
              <Plane className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
              {customerName ? (
                <span
                  className={`aero:text-[11px] aero:leading-[14px] aero:truncate ${textClass}`}
                >
                  {customerName}
                </span>
              ) : (
                <span className="aero:text-[11px] aero:leading-[14px] aero:truncate aero:italic aero:text-muted-foreground">
                  Unassigned Customer
                </span>
              )}
            </div>
          )}
          {!hideTechnicianRow && barHeight >= 100 && (
            <div className="aero:flex aero:items-center aero:gap-1.5">
              <User className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
              {technicianName ? (
                <span
                  className={`aero:text-[11px] aero:leading-[14px] aero:truncate ${textClass}`}
                >
                  {technicianName}
                </span>
              ) : (
                <span className="aero:text-[11px] aero:leading-[14px] aero:truncate aero:italic aero:text-muted-foreground">
                  Unassigned Technician
                </span>
              )}
            </div>
          )}
          {!hideMachineRow && barHeight >= 115 && (
            <div className="aero:flex aero:items-center aero:gap-1.5">
              <Cog className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
              {machineName ? (
                <span
                  className={`aero:text-[11px] aero:leading-[14px] aero:truncate ${textClass}`}
                >
                  {machineName}
                </span>
              ) : (
                <span className="aero:text-[11px] aero:leading-[14px] aero:truncate aero:italic aero:text-muted-foreground">
                  Unassigned Machine
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function JobGanttChart({
  jobs,
  isLoading = false,
  searchValue,
  onSearchChange,
  filterableAttributes,
  dynamicFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  sortableAttributes,
  dynamicSorts,
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  dateRange,
  onDateRangeChange,
  viewMode,
  onViewModeChange,
  zoomLevel = 100,
  onZoomIn,
  onZoomOut
}: JobGanttChartProps) {
  const colWidth = getColWidth(viewMode, zoomLevel);
  const { isClient, currentDay, timelineData, timeSlots, formatTime } =
    useScheduleCalendar("weekly", {
      externalDateRange: dateRange,
      externalViewMode: viewMode,
      onViewModeChange
    });

  const processedAssignments = useMemo((): ProcessedAssignment[] => {
    const allAssignments: ProcessedAssignment[] = [];

    jobs.forEach((job) => {
      (job.assignments || []).forEach((assignment) => {
        allAssignments.push({
          ...assignment,
          startDate: assignment.startDate,
          eddDate: assignment.eddDate,
          priorityColor: getPriorityColor(assignment.priority || 1),
          baseJobId: job.jobOrderNo,
          customerName: job.customerName
        });
      });
    });

    return allAssignments;
  }, [jobs]);

  const groupedByJob = useMemo(() => {
    const groups: Record<
      string,
      { assignments: ProcessedAssignment[]; laneCount: number }
    > = {};
    processedAssignments.forEach((assignment) => {
      const baseJobId = assignment.baseJobId ?? "";
      if (!groups[baseJobId]) {
        groups[baseJobId] = { assignments: [], laneCount: 1 };
      }
      groups[baseJobId].assignments.push(assignment);
    });

    Object.keys(groups).forEach((jobId) => {
      const { assignments, laneCount } = calculateLanes(
        groups[jobId].assignments
      );
      groups[jobId] = { assignments, laneCount };
    });

    return groups;
  }, [processedAssignments]);

  // Two-phase selection: check cards (pending) → click Select to apply filter
  const [pendingJobIds, setPendingJobIds] = useState<string[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<string[]>([]);
  useEffect(() => {
    setPendingJobIds([]);
    setAppliedJobIds([]);
  }, [viewMode]);
  const filteredJobs =
    appliedJobIds.length > 0
      ? jobs.filter((j) => appliedJobIds.includes(j.jobOrderNo))
      : jobs;

  const monthlyRows = useMemo<MonthlyCalendarRow[]>(
    () =>
      filteredJobs.map((job) => {
        const assignments = groupedByJob[job.jobOrderNo]?.assignments ?? [];
        const isSelected = pendingJobIds.includes(job.jobOrderNo);
        return {
          id: job.jobOrderNo,
          assignments,
          rowCard: OverviewJobSchedule(
            job,
            assignments,
            job.jobOrderNo,
            () =>
              setPendingJobIds((prev) =>
                prev.includes(job.jobOrderNo)
                  ? prev.filter((id) => id !== job.jobOrderNo)
                  : [...prev, job.jobOrderNo]
              ),
            isSelected
          )
        };
      }),
    [filteredJobs, groupedByJob, pendingJobIds]
  );

  const selectButton =
    appliedJobIds.length > 0 ? (
      <button
        onClick={() => {
          setAppliedJobIds([]);
          setPendingJobIds([]);
        }}
        className="aero:text-[11px] aero:font-medium aero:text-brand-primary-600 aero:hover:underline aero:leading-none"
      >
        Unselect
      </button>
    ) : pendingJobIds.length > 0 ? (
      <button
        onClick={() => setAppliedJobIds(pendingJobIds)}
        className="aero:text-[11px] aero:font-medium aero:text-brand-primary-600 aero:hover:underline aero:leading-none"
      >
        Select
      </button>
    ) : null;

  return (
    <ScheduleCalendarView
      title="Job Schedule Calendar"
      isLoading={isLoading || !isClient}
      headerAction={
        <Button
          design="solid"
          size="sm"
          className="aero:gap-1.5 aero:bg-blue-800"
        >
          <Plus className="aero:h-4 aero:w-4" />
          Create
        </Button>
      }
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      filterableAttributes={filterableAttributes}
      dynamicFilters={dynamicFilters}
      onAddFilter={onAddFilter}
      onUpdateFilter={onUpdateFilter}
      onRemoveFilter={onRemoveFilter}
      onClearFilters={onClearFilters}
      sortableAttributes={sortableAttributes}
      dynamicSorts={dynamicSorts}
      onAddSort={onAddSort}
      onUpdateSort={onUpdateSort}
      onRemoveSort={onRemoveSort}
      onClearSorts={onClearSorts}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      zoomLevel={zoomLevel}
      onZoomIn={onZoomIn}
      onZoomOut={onZoomOut}
    >
      {jobs.length === 0 ? (
        viewMode === "monthly" ? (
          <MonthlyCalendarGrid
            days={timelineData.days}
            rows={[]}
            zoomLevel={zoomLevel}
            rowLabelHeader="Job Orders"
            rowLabelDescription="No jobs scheduled. Data will appear when schedule is available."
          />
        ) : (
          <ScheduleCalendarContent
            viewMode={viewMode}
            timelineData={timelineData}
            timeSlots={timeSlots}
            isEmpty={true}
            emptyMessage="No jobs scheduled. Data will appear when schedule is available."
            rowLabelHeader="Job Orders"
            rowLabelDescription="Select a job card below to see more details"
            zoomLevel={zoomLevel}
            leftPanelWidth={SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH}
            rowData={[]}
          />
        )
      ) : viewMode === "daily" ? (
        /* Daily View - Timeline */
        <ScheduleCalendarContent
          viewMode={viewMode}
          timelineData={timelineData}
          timeSlots={timeSlots}
          isEmpty={false}
          rowLabelHeader="Job Orders"
          rowLabelDescription={"Select a job card below to see more details"}
          headerAction={selectButton}
          zoomLevel={zoomLevel}
          leftPanelWidth={SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH}
          rowData={filteredJobs
            .map((job) => {
              const jobNo = job.jobOrderNo;
              const jobData = groupedByJob[jobNo];
              if (!jobData) return null;

              const laneHeight = getDailyLaneHeight(zoomLevel);

              // Only count lanes for assignments VISIBLE on the current day
              // (avoids giant empty rows when a job has multi-day tasks on other days)
              const visibleAssignments = jobData.assignments.filter((a) => {
                if (!a.startDate) return false;
                const startDate = dayjs(a.startDate);
                const endDate = a.eddDate ? dayjs(a.eddDate) : startDate;
                const viewDate = dayjs(currentDay);
                return (
                  !endDate.isBefore(viewDate, "day") &&
                  !startDate.isAfter(viewDate, "day")
                );
              });
              const visibleMaxLane = visibleAssignments.reduce(
                (max, a) => Math.max(max, (a as any).lane || 0),
                0
              );
              const visibleLaneCount =
                visibleAssignments.length > 0 ? visibleMaxLane + 1 : 1;
              const ROW_PAD = SCHEDULE_CONSTANTS.ROW_PAD;
              const rowHeight = Math.max(
                visibleLaneCount * laneHeight + ROW_PAD * 2,
                laneHeight + ROW_PAD * 2,
                SCHEDULE_CONSTANTS.SIDEBAR_ROW_MIN_HEIGHT
              );

              return {
                key: job.id,
                height: rowHeight,
                left: OverviewJobSchedule(
                  job,
                  jobData.assignments,
                  jobNo,
                  () =>
                    setPendingJobIds((prev) =>
                      prev.includes(jobNo)
                        ? prev.filter((id) => id !== jobNo)
                        : [...prev, jobNo]
                    ),
                  pendingJobIds.includes(jobNo)
                ),
                right: (
                  <div
                    className="aero:relative"
                    style={{
                      minHeight: `${rowHeight}px`,
                      width: `${SCHEDULE_CONSTANTS.TOTAL_COLUMNS * colWidth}px`,
                      flexShrink: 0
                    }}
                  >
                    {/* Grid lines for each hour - 8:00-17:00 */}
                    <DailyGridLines colWidth={colWidth} day={currentDay} />

                    {/* Lane dividers */}
                    <LaneDividers
                      laneCount={visibleLaneCount}
                      laneHeight={laneHeight}
                    />

                    {/* Assignment bars — use pre-filtered visibleAssignments */}
                    {visibleAssignments.map((assignment) => {
                      const pos = getAssignmentPosition(
                        assignment,
                        currentDay,
                        zoomLevel
                      );
                      if (!pos) return null;
                      const { leftPx, widthPx, isStartClipped, isEndClipped } =
                        pos;

                      const lane = (assignment as any).lane || 0;
                      const topOffset = ROW_PAD + lane * laneHeight;
                      const barHeight = laneHeight - ROW_PAD; // ROW_PAD inset top, ~same bottom
                      const {
                        color: barColor,
                        text: barText,
                        accent: barAccent
                      } = getJobBarColorForTime(
                        assignment.startDate,
                        assignment.barVariant
                      );
                      return (
                        <ScheduleBarTooltip
                          key={assignment.id}
                          variant="job"
                          data={{
                            jobOrderNo: assignment.jobOrderNo,
                            processDesc: assignment.processDesc,
                            partNo: assignment.partNo,
                            partDescription: assignment.partDescription,
                            customerName: (assignment as ProcessedAssignment)
                              .customerName,
                            startDate: assignment.startDate,
                            eddDate: assignment.eddDate,
                            duration: assignment.duration,
                            technicianName: assignment.technicianName,
                            machineName: assignment.machineName,
                            priorityColor: barColor,
                            unavailableReason: assignment.barUnavailableReason
                          }}
                        >
                          <div
                            className={getAssignmentBarClasses(
                              barColor,
                              isStartClipped,
                              isEndClipped,
                              true,
                              barText,
                              "dashed-full"
                            )}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`,
                              top: `${topOffset}px`,
                              height: `${barHeight}px`
                            }}
                          >
                            {isStartClipped && (
                              <ClippedIndicator position="start" />
                            )}
                            {isEndClipped && (
                              <ClippedIndicator position="end" />
                            )}
                            {/* Solid left accent bar */}
                            <div
                              className={`aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] ${barAccent} aero:rounded-l`}
                            />
                            <JobBarContent
                              startDate={assignment.startDate}
                              eddDate={assignment.eddDate}
                              duration={assignment.duration}
                              processDesc={assignment.processDesc}
                              jobOrderNo={assignment.jobOrderNo}
                              partNo={assignment.partNo}
                              partDescription={assignment.partDescription}
                              customerName={
                                (assignment as ProcessedAssignment).customerName
                              }
                              technicianName={assignment.technicianName}
                              machineName={assignment.machineName}
                              barHeight={barHeight}
                              textClass={barText}
                              formatTime={formatTime}
                            />
                          </div>
                        </ScheduleBarTooltip>
                      );
                    })}
                  </div>
                )
              };
            })
            .filter((r) => r !== null)}
        />
      ) : viewMode === "monthly" ? (
        /* Monthly View - Calendar Grid */
        <MonthlyCalendarGrid
          days={timelineData.days}
          rows={monthlyRows}
          zoomLevel={zoomLevel}
          rowLabelHeader="Job Orders"
          rowLabelDescription={"Select a job card below to see more details"}
          headerAction={selectButton}
        />
      ) : (
        /* Weekly View - Two-column layout (matches monthly) */
        <WeeklyCalendarGrid
          days={timelineData.days}
          jobs={filteredJobs}
          groupedByJob={groupedByJob}
          selectedJobIds={pendingJobIds}
          onJobSelect={setPendingJobIds}
          zoomLevel={zoomLevel}
          rowLabelHeader="Job Orders"
          rowLabelDescription={"Select a job card below to see more details"}
          headerAction={selectButton}
          showDetailView={appliedJobIds.length === 1}
          formatTime={formatTime}
        />
      )}
    </ScheduleCalendarView>
  );
}
